import { getPeriode, getPeriodeCustom } from "@/services/getData";
import { CustomPeriode, Periode } from "@/types";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import useSessionStorage from "./useSessionStorage";
import { useUser } from "./useUser";

// Helper to parse date strings and check against current time
const checkHasPassed = (
  dateString?: string | null,
  serverTime?: string | null,
): boolean | null => {
  if (!dateString) return null;
  // Handle SQL format (YYYY-MM-DD HH:MM:SS) compatibility for Safari/valid ISO
  const cleanDate = dateString.replace(" ", "T");
  const currentTime = serverTime
    ? new Date(serverTime.replace(" ", "T"))
    : new Date();
  return currentTime > new Date(cleanDate);
};

// Helper for exact start time checks (>=)
const checkHasStarted = (
  dateString?: string | null,
  serverTime?: string | null,
): boolean | null => {
  if (!dateString) return null;
  const cleanDate = dateString.replace(" ", "T");
  const currentTime = serverTime
    ? new Date(serverTime.replace(" ", "T"))
    : new Date();
  return currentTime >= new Date(cleanDate);
};

export default function useTimePeriode() {
  const { user } = useUser();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const periodId = sessionPeriode?.id_sys_periode;

  // 1. Queries
  const periodeQuery = useQuery<Periode>({
    queryKey: ["periode", periodId],
    queryFn: () => getPeriode(periodId as string),
    enabled: !!periodId && !!user,
    refetchOnWindowFocus: false,
  });

  const customPeriodeQuery = useQuery<CustomPeriode | null>({
    queryKey: ["periodeCustom", periodId],
    queryFn: () => getPeriodeCustom(periodId as string),
    enabled: !!periodId && !!user,
    refetchOnWindowFocus: false,
  });

  const dataPeriode = periodeQuery.data;
  const dataPeriodeCustom = customPeriodeQuery.data;

  // 2. Derived State (Standard Period)
  const timePassed = useMemo(
    () => ({
      waktu_pengumuman: checkHasStarted(
        dataPeriode?.waktu_pengumuman,
        dataPeriode?.waktu_current_server,
      ),
      waktu_pengumuman_kelulusan: checkHasStarted(
        dataPeriode?.waktu_pengumuman_kelulusan,
        dataPeriode?.waktu_current_server,
      ),
      waktu_selesai_daftar: checkHasPassed(
        dataPeriode?.waktu_selesai_daftar,
        dataPeriode?.waktu_current_server,
      ),
      waktu_selesai_daftar_valid: checkHasPassed(
        dataPeriode?.waktu_selesai_daftar_valid,
        dataPeriode?.waktu_current_server,
      ),
      waktu_selesai_isi_ukt: checkHasPassed(
        dataPeriode?.waktu_selesai_isi_ukt,
        dataPeriode?.waktu_current_server,
      ),
      waktu_selesai_bayar_ukt: checkHasPassed(
        dataPeriode?.waktu_selesai_bayar_ukt,
        dataPeriode?.waktu_current_server,
      ),
    }),
    [dataPeriode],
  );

  // 3. Derived State (Custom Period)
  const timePassedCustom = useMemo(
    () => ({
      custom_waktu_selesai_daftar_valid: checkHasPassed(
        dataPeriodeCustom?.custom_waktu_selesai_daftar_valid,
        dataPeriode?.waktu_current_server,
      ),
      custom_waktu_selesai_isi_ukt: checkHasPassed(
        dataPeriodeCustom?.custom_waktu_selesai_isi_ukt,
        dataPeriode?.waktu_current_server,
      ),
      custom_waktu_selesai_bayar_ukt: checkHasPassed(
        dataPeriodeCustom?.custom_waktu_selesai_bayar_ukt,
        dataPeriode?.waktu_current_server,
      ),
      custom_waktu_selesai_daftarkembali: checkHasPassed(
        dataPeriodeCustom?.custom_waktu_selesai_daftarkembali,
        dataPeriode?.waktu_current_server,
      ),
      custom_waktu_pengumuman_ukt: checkHasPassed(
        dataPeriodeCustom?.custom_waktu_pengumuman_ukt,
        dataPeriode?.waktu_current_server,
      ),
    }),
    [dataPeriodeCustom, dataPeriode],
  );

  // 4. Derived State (Active Logic Strategy)
  // Consolidates logic: If Custom exists, use Custom, otherwise use Standard.
  const activeConfig = useMemo(() => {
    const isCustom = !!dataPeriodeCustom;

    const dataPeriodeActive = isCustom
      ? {
          waktu_selesai_daftar_valid:
            dataPeriodeCustom.custom_waktu_selesai_daftar_valid,
          waktu_selesai_isi_ukt: dataPeriodeCustom.custom_waktu_selesai_isi_ukt,
          waktu_selesai_bayar_ukt:
            dataPeriodeCustom.custom_waktu_selesai_bayar_ukt,
          waktu_selesai_sanggah_berkas_daftarkembali:
            dataPeriodeCustom.custom_waktu_selesai_sanggah_berkas_daftarkembali,
        }
      : {
          waktu_selesai_daftar_valid: dataPeriode?.waktu_selesai_daftar_valid,
          waktu_selesai_isi_ukt: dataPeriode?.waktu_selesai_isi_ukt,
          waktu_selesai_bayar_ukt: dataPeriode?.waktu_selesai_bayar_ukt,
          waktu_selesai_sanggah_berkas_daftarkembali:
            dataPeriode?.waktu_selesai_sanggah_berkas_daftarkembali,
        };

    const timePassedActive = isCustom
      ? {
          waktu_selesai_daftar_valid_selesai:
            timePassedCustom.custom_waktu_selesai_daftar_valid,
          waktu_selesai_isi_ukt_selesai:
            timePassedCustom.custom_waktu_selesai_isi_ukt,
          waktu_selesai_bayar_ukt_selesai:
            timePassedCustom.custom_waktu_selesai_bayar_ukt,
          waktu_pengumuman: timePassedCustom.custom_waktu_pengumuman_ukt,
        }
      : {
          waktu_selesai_daftar_valid_selesai:
            timePassed.waktu_selesai_daftar_valid,
          waktu_selesai_isi_ukt_selesai: timePassed.waktu_selesai_isi_ukt,
          waktu_selesai_bayar_ukt_selesai: timePassed.waktu_selesai_bayar_ukt,
          waktu_pengumuman: timePassed.waktu_pengumuman,
        };

    return { dataPeriodeActive, timePassedActive };
  }, [dataPeriode, dataPeriodeCustom, timePassed, timePassedCustom]);

  return {
    // React Query Status
    isFetched: periodeQuery.isFetched,
    isPendingPeriode: periodeQuery.isPending,
    isLoadingPeriode: periodeQuery.isLoading,
    isPendingPeriodeCustom: customPeriodeQuery.isPending,
    isFetchedPeriodeCustom: customPeriodeQuery.isFetched,

    // Data
    dataPeriode,
    dataPeriodeCustom,

    // Calculated Times
    timePassed,
    timePassedCustom,

    // Active Logic
    timePassedActive: activeConfig.timePassedActive,
    dataPeriodeActive: activeConfig.dataPeriodeActive,
  };
}
